(ns app.bundle-social.platforms
  "The platforms Storrito posts on through bundle.social.

   Every platform is a vertical slice of its own
   (`slices/bundle-social-<platform>`) whose `platform` map describes it
   as data; the bundle-social slice, the scheduling page, the agenda and
   the connect modal read that map instead of knowing any platform. A
   new platform is a new slice plus one line in `platforms`, like a new
   slice is one line in `app.system.register`.

   The post type ids (`:post-type/id`), option keys (`:option/key`)
   and choice values (`:choice/value`) are public API: clients of
   `list-social-accounts` and `schedule-social-post` send them, so
   renaming one breaks them.

   Platform keys:

   - `:social-platform/id`: bundle.social's socialAccountType, e.g.
     \"FACEBOOK\"; stored as `:social.account/platform`.
   - `:social-platform/key`: the keyword of the platform in the UI (agenda
     account kind, activity platform).
   - `:social-platform/name`, `/account-noun`: labels.
   - `:social-platform/channel-noun`, `/channel-nouns`: what the page
     picker offers after the OAuth (\"page\", \"pages\"), for a platform
     whose account publishes on a page or channel chosen afterwards.
   - `:social-platform/icon`: `(fn [classes] hiccup)`.
   - `:social-platform/text-class`, `/pill-class`: Tailwind classes.
   - `:social-platform/launched?`: offered to every user. An unlaunched
     platform is offered only on `/ui/social-accounts?platform=<id>` and,
     once an account of it is connected, on the scheduling page of that
     organization: the internal test before the launch.
   - `:social-platform/business-scope?`: ask for Meta's business scope
     before the OAuth.
   - `:social-platform/connect-options`: further fields of the connect
     request, e.g. Instagram's `instagramConnectionMethod`.
   - `:social-platform/line-item-name`: the platform's name on the
     line-item of a published post, instead of `:social-platform/name`.
   - `:social-platform/username-as-name?`: the account's name is its
     username rather than its display name (Instagram's fallback
     connection is matched by username).
   - `:social-platform/accounts-page-label`: the page where an account
     of a hidden platform is connected, named in the push's error
     messages instead of `accounts-page-label`.
   - `:social-platform/return-path`: that page's path, where the OAuth
     may return to instead of the accounts page.
   - `:social-platform/callback-messages`: the errors the platform's
     OAuth reports on the redirect url, to their message. bundle.social
     reports them as a parameter of their own (Meta's
     `facebook-not-enough-pages`) or as the value of `error` (Snapchat's
     `error=snapchat-no-public-profile`); see `callback-message`.
   - `:social-platform/count-noun`, `/count-nouns`: what the Social
     Accounts page counts (\"3 pages\"); defaults to the channel nouns,
     then \"profile\"/\"profiles\".
   - `:social-platform/connect-card`: the card in the connect modal and
     on the Social Accounts page; its `:style :rail-class` is the brand
     colour of the card's top rail.
   - `:social-platform/connect-hint`: `{:title :body :points
     :account-type-notes :help-url :help-label}`, shown in a modal
     before the OAuth, for a platform whose accounts need a preparation
     (Snapchat's Public Profile) or that costs extra (X, Instagram's
     fallback);
     `:points` is an optional list of short paragraphs below the body,
     `:account-type-notes` an optional map of an account type (the
     modal's `:account-type` param) to a highlighted note. Body, points
     and notes are strings or hiccup (bold key words).
   - `:social-platform/own-integration`: `{:delete-command :id-key
     :same-ids?}` of Storrito's own integration of the network (YouTube,
     TikTok). Its accounts are listed on the Social Accounts page as well
     and removed with its own delete command; with `:same-ids?` an
     account connected both ways is one row (YouTube's channel id). Until
     the platform is launched, its card connects through the own
     integration's OAuth (`:social-accounts/own-connect-urls`).
   - `:social-platform/post-channel?`: the platform's posts name their
     channel (Pinterest's board) instead of a set-channel at
     bundle.social. The page picker's choice becomes a Storrito account
     per channel whose `:social.account/platform-id` is the channel id,
     handed to `post-data` as `:channel`. The push routine resolves the
     id to the channel's current name from the account's team and hands
     it over as `:channel-name` (Pinterest's `boardName`).
   - `:social-platform/post-data`: `(fn [{:keys [post-type media title
     text options upload-id channel channel-name cover-url]}] data)`, the platform's
     entry in the `data` of a bundle.social post; `:title` is the story's
     name.
   - `:social-platform/post-types`: what can be posted, each with
     `:post-type/id` (bundle.social's type, stored in
     `:social.post/data`), `/kind` (the scheduling page's destination
     kind), `/label`, `/group-label`, `/mark`, `/media` (a set of
     `:video` and `:image`), `/min-duration-seconds`,
     `/max-duration-seconds`, `/caption?` (the post publishes the
     caption), `/text-required?`, `/max-text-length` (a longer
     caption is shortened, see `fit-caption`), `/max-text-bytes` (the
     same in UTF-8 bytes, YouTube; the platform's `post-data` shortens
     it), `/cover?` (a video post
     gets the preview image as `:cover-url`), `/cover-required?` (the
     post fails when the cover cannot be uploaded; otherwise it goes
     without one) and `/options`.
   - `:post-type/destination-group`: the destination group of the
     scheduling page's account list the post type joins, when it is not
     a group of its own: `:youtube` puts bundle.social's YouTube channels
     next to the ones of Storrito's own integration.
   - `:post-type/options-card`: the options card of Storrito's own
     integration the post type shares (`:tiktok`, `:youtube`), instead of the
     generic options of `:post-type/options`; the scheduling page maps
     the card's fields to the options.
   - `:post-type/options`: what the scheduling page asks per post type,
     each `{:option/key :option/label :option/hint}` and either
     `:option/choices [{:choice/value :choice/label}]` (the first is the
     default), `:option/type :url` (an optional link) or `:option/type
     :text` (an optional text with `:option/max-length`; `:option/default
     :media-name` prefills the media's name) or `:option/type :checkbox`
     (`true` when ticked, absent otherwise) or `:option/type :number`
     (optional, within `:option/min`/`:option/max`). A choice option
     with `:option/required?` has no default; `:option/error` words the
     message of an invalid value. The values reach `post-data` as
     `:options`.
   - `:post-type/options-check`: `(fn [resolved-options] error-or-nil)`,
     a rule across options (TikTok: no paid partnership that only the
     creator can see).
   - `:post-type/jpeg-images?`: the post type takes JPEG images only;
     the push converts a PNG image (TikTok's photo mode).
   - `:post-type/service`: the service of `app.subscription.services` a
     published post is billed as, `:social-post` when absent;
     `:post-type/link-service` the one when its caption contains a link
     (X charges more for those), see `app.bundle-social.billing`.
   - `:post-type/always-billed?`: billed for legacy subscriptions (Paddle
     Classic) too, which pay no other social post, so posting it needs a
     positive credit balance (X charges Storrito for every post), see
     `credits-allow?`.
   - `:post-type/fit-text`: `(fn [text] text)`, the cut `fit-caption`
     uses instead of `:post-type/max-text-length` (X's weighted length),
     clj only; the schedule command stores its result. The platform's
     `post-data` and billing apply it again, which changes nothing on an
     already fitted caption."
  (:require [app.bundle-social-facebook.platform :as facebook]
            [app.bundle-social-instagram.platform :as instagram]
            [app.bundle-social-linkedin.platform :as linkedin]
            [app.bundle-social-pinterest.platform :as pinterest]
            [app.bundle-social-snapchat.platform :as snapchat]
            [app.bundle-social-threads.platform :as threads]
            [app.bundle-social-tiktok.platform :as tiktok]
            [app.bundle-social-x.platform :as x]
            [app.bundle-social-youtube.platform :as youtube]
            [app.bundle-social.text :as text]
            [clojure.string :as str]))

(def platforms
  [facebook/platform
   linkedin/platform
   pinterest/platform
   snapchat/platform
   threads/platform
   tiktok/platform
   x/platform
   youtube/platform])

(def hidden-platforms
  "Platforms no page offers and no user schedules on: the catalog
   functions that list platforms or post types leave them out, and so
   do the queries and commands that read the social accounts (see
   `visible-account?`). Only `by-id` finds them, for the connect, the
   sync and the push of their posts. Instagram is the fallback
   connection of Storrito's own Instagram integration."
  [instagram/platform])

(defn hidden?
  "The platform `id` is one of the `hidden-platforms`."
  [id]
  (boolean (some #(= id (:social-platform/id %)) hidden-platforms)))

(defn visible-account?
  "The social account is shown and can be scheduled on: its platform is
   not hidden."
  [account]
  (not (hidden? (:social.account/platform account))))

(def accounts-page-label
  "The sidebar label of the accounts page, `/ui/social-accounts`."
  "Social Accounts")

(defn by-id
  "The platform `id`, a hidden one included."
  [id]
  (some #(when (= id (:social-platform/id %)) %)
        (concat platforms hidden-platforms)))

(defn by-key
  [k]
  (some #(when (= k (:social-platform/key %)) %) platforms))

(defn launched
  []
  (filter :social-platform/launched? platforms))

(defn post-type
  "The post type `type-id` of the platform `id`."
  [id type-id]
  (some #(when (= type-id (:post-type/id %)) %)
        (:social-platform/post-types (by-id id))))

(defn post-types
  "All post types, each with its platform as `:social-platform/id`."
  []
  (for [platform platforms
        post-type (:social-platform/post-types platform)]
    (assoc post-type :social-platform/id (:social-platform/id platform))))

(defn post-type-by-kind
  [kind]
  (some #(when (= kind (:post-type/kind %)) %) (post-types)))

(defn launched-kinds
  "The destination kinds of the launched platforms, in platform order."
  []
  (for [platform (launched)
        post-type (:social-platform/post-types platform)]
    (:post-type/kind post-type)))

(defn post-type-label
  "\"Facebook Reel\"; the raw ids for a platform that is gone."
  [id type-id]
  (or (:post-type/label (post-type id type-id))
      (str id " " type-id)))

(defn account-noun
  [id]
  (or (:social-platform/account-noun (by-id id))
      "social account"))

(defn icon
  [id classes]
  (when-let [icon-fn (:social-platform/icon (by-id id))]
    (icon-fn classes)))

(defn credits-allow?
  "The organization may post the post type `type-id` of the platform
   `id`: with a positive credit balance, or with a legacy subscription
   (Paddle Classic), which pays no social posts, unless the post type is
   `:post-type/always-billed?`. Shared by the schedule command, the push
   and the scheduling page."
  [id type-id {:organization/keys [positive-credit-balance? legacy-customer?]}]
  (boolean
    (or positive-credit-balance?
        (and legacy-customer?
             (not (:post-type/always-billed? (post-type id type-id)))))))

(defn caption-error
  "The problem of `caption` for the post type `type-id` of the platform
   `id`, nil when there is none: missing where the platform requires a
   caption (LinkedIn). A post type that does not publish the caption
   ignores it; a caption that is too long is shortened (`fit-caption`).
   Shared by the schedule command and the scheduling page, which puts
   the platform in front of the message."
  [id type-id caption]
  (let [{:post-type/keys [caption? text-required?]} (post-type id type-id)]
    (when (and caption?
               text-required?
               (str/blank? caption))
      "Add a caption.")))

(defn fit-caption
  "The trimmed `caption` as the post type publishes it: shortened to its
   `:post-type/max-text-length`, or by its `:post-type/fit-text` (X's
   weighted length, which a character count would cut inside links).
   The whole caption is shared by all destinations of the scheduling
   page, so it is fitted per platform instead of rejected."
  [id type-id caption]
  (let [text (str/trim (or caption ""))
        {:post-type/keys [max-text-length fit-text]} (post-type id type-id)]
    (cond
      fit-text (fit-text text)
      (and max-text-length (< max-text-length (count text))) (text/shorten text max-text-length)
      :else text)))

(defn caption-shortened?
  "The post type publishes the caption shortened. With a
   `:post-type/fit-text` (clj only, X) its result decides; the page
   (cljs) estimates by `:post-type/max-text-length`."
  [id type-id caption]
  (let [{:post-type/keys [caption? max-text-length max-text-bytes fit-text]} (post-type id type-id)
        text (str/trim (or caption ""))]
    (boolean
      (and caption?
           (if fit-text
             (not= text (fit-text text))
             (or (and max-text-length
                      (< max-text-length (count text)))
                 (and max-text-bytes
                      (< max-text-bytes (text/utf8-length text)))))))))

(defn options
  "The `:post-type/options` of the post type."
  [id type-id]
  (:post-type/options (post-type id type-id)))

(defn resolve-option
  "The value of one option from the chosen `values`, or `::invalid`.
   A choice option falls back to its first choice, unless it is
   `:option/required?` (TikTok's privacy has no default); a `:url` or
   `:text` option is optional and dropped when blank; a `:checkbox`
   option is `true` when ticked and dropped otherwise; a `:number` is an
   optional integer within `:option/min` and `:option/max`."
  [{:option/keys [key type choices max-length required?] :as option} values]
  (let [value (get values key)
        lowest (:option/min option)
        highest (:option/max option)]
    (case type
      :checkbox (when (true? value) true)
      :number (cond
                (nil? value) nil
                ;; An integer, which also rules out NaN and Infinity.
                (not (integer? value)) ::invalid
                (and lowest (< value lowest)) ::invalid
                (and highest (< highest value)) ::invalid
                :else value)
      :text (let [value (some-> value str/trim not-empty)]
              (cond
                (nil? value) nil
                (and max-length (< max-length (count value))) ::invalid
                :else value))
      :url (let [value (some-> value str/trim not-empty)]
             (cond
               (nil? value) nil
               (text/url? value) value
               :else ::invalid))
      (let [value (or value (when-not required?
                              (:choice/value (first choices))))]
        (if (contains? (set (map :choice/value choices)) value)
          value
          ::invalid)))))

(defn option-error
  "Why the chosen value of `option` is invalid, for the scheduling page;
   `:option/error` overrides it."
  [{:option/keys [type label max-length error]}]
  (or error
      (case type
        :url "Enter a link that starts with https://."
        :text (str label " can be at most " max-length " characters long.")
        (str "Choose a valid " (str/lower-case label) "."))))

(defn resolve-options*
  "The chosen `values` completed with the defaults, or `::invalid` with
   the message of the first invalid value as `:error`."
  [id type-id values]
  (let [post-type (post-type id type-id)
        resolved (reduce (fn [resolved option]
                           (let [value (resolve-option option values)]
                             (cond
                               (= ::invalid value) (reduced {::invalid option})
                               (nil? value) resolved
                               :else (assoc resolved (:option/key option) value))))
                         {}
                         (:post-type/options post-type))]
    (if-let [option (::invalid resolved)]
      {::invalid true :error (option-error option)}
      (if-let [error (when-let [check (:post-type/options-check post-type)]
                       (check resolved))]
        {::invalid true :error error}
        resolved))))

(defn resolve-options
  "The chosen `values` (option key to value) completed with the
   defaults. Nil when a value is invalid or the combination fails the
   post type's `:post-type/options-check`; values of unknown options are
   dropped, and so are empty optional ones."
  [id type-id values]
  (let [resolved (resolve-options* id type-id values)]
    (when-not (::invalid resolved)
      resolved)))

(defn options-error
  "The message of the first invalid chosen value or of the post type's
   `:post-type/options-check`, nil when all are valid."
  [id type-id values]
  (:error (resolve-options* id type-id values)))

(defn option-default
  "The value a `:text` option is prefilled with: the media's name
   (already without its file extension) for `:option/default
   :media-name`, shortened to fit, as the push shortens the fallback."
  [{:option/keys [default max-length]} media-name]
  (case default
    :media-name (some-> media-name (cond-> max-length (text/shorten max-length)))
    nil))

(defn count-label
  "\"3 pages\", \"1 profile\": how many accounts of `platform` are
   connected, for the Social Accounts page."
  [platform n]
  (str n " "
       (if (= 1 n)
         (or (:social-platform/count-noun platform)
             (:social-platform/channel-noun platform)
             "profile")
         (or (:social-platform/count-nouns platform)
             (:social-platform/channel-nouns platform)
             "profiles"))))

(defn shown-platforms
  "The platforms the Social Accounts page shows a card for: the
   `offered` ones, then every other platform of the catalog the
   organization has `accounts` of (connected during an internal test),
   each once."
  [offered accounts]
  (let [connected (set (map :social.account/platform accounts))
        offered-ids (set (map :social-platform/id offered))]
    (into (vec offered)
          (filter #(and (connected (:social-platform/id %))
                        (not (offered-ids (:social-platform/id %)))))
          platforms)))

(defn unknown-accounts
  "The `accounts` of a platform that is not in the catalog (removed, or
   reported by bundle.social without Storrito supporting it), so the
   page can still list and remove them."
  [accounts]
  (remove (comp by-id :social.account/platform) accounts))

(defn callback-messages
  "The known OAuth errors of all platforms, to their message."
  []
  (into {} (mapcat :social-platform/callback-messages) platforms))

(defn error-platform
  "The platform an OAuth error code names by its prefix, e.g. Snapchat
   for \"snapchat-no-public-profile\"."
  [code]
  (some #(when (str/starts-with? code (str (name (:social-platform/key %)) "-")) %)
        platforms))

(defn callback-message
  "The message for the OAuth error on the redirect url's
   `query-params`, nil when the connect did not fail. A known error has
   its own message; any other `error` is still reported, with the
   platform it names, so a failed connect never ends silently."
  [query-params]
  (let [messages (callback-messages)
        code (some-> (:error query-params) str str/trim not-empty)]
    (or (some (fn [[param message]]
                (when (contains? query-params param)
                  message))
              messages)
        (when code
          (or (get messages (keyword code))
              (str (if-let [platform (error-platform code)]
                     (str (:social-platform/name platform) " did not connect the account")
                     "The account could not be connected")
                   " (" code "). Please try again, and contact us if it keeps happening."))))))
